import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Textarea,
} from "@chakra-ui/react";
import { FormFieldType } from "@models/form";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import { REGEX_EMAIL } from "@models/regex";
import { Dispatch, SetStateAction, useState } from "react";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { BiHide, BiShow } from "react-icons/bi";
import TextareaAutosize from "react-textarea-autosize";
import DynamicFormFieldHeader from "./DynamicFormFieldHeader";
import { Field } from "./types";

const PasswordShowButton = ({
  show,
  setShow,
}: {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <button
      className="h-full p-2 absolute top-0 right-0 flex flex-row items-center text-neutral-500 text-xl"
      onClick={() => {
        setShow(!show);
      }}
    >
      {show ? <BiHide /> : <BiShow />}
    </button>
  );
};

export interface ArrayFieldProps {
  fieldArrayName: string;
  arrayFieldIndex: number;
}

const DynamicFormInput = ({
  field,
  errors,
  register,
  setValue,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
  fieldArrayProps,
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
  setValue?: UseFormSetValue<FieldValues>;
  fieldArrayProps?: ArrayFieldProps;
}) => {
  const [show, setShow] = useState(false);

  // TODO: default translation
  const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const toRegisterOptions = (
    field: Field
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.minLength
        ? {
            minLength: {
              value: field.minLength,
              message:
                t(field.errorMessages?.minLength, language) ||
                _t(`Minimum length is ${field.max}`, language),
            },
          }
        : {}),
      ...(field.maxLength
        ? {
            maxLength: {
              value: field.maxLength,
              message:
                t(field.errorMessages?.maxLength, language) ||
                _t(`Maximum length is ${field.max}`, language),
            },
          }
        : {}),
      ...(field.pattern
        ? {
            pattern: {
              value: new RegExp(field.pattern, "gm"),
              message:
                t(field.errorMessages?.pattern, language) ||
                _t("The input is not valid", language),
            },
          }
        : field.type === FormFieldType.EMAIL
        ? {
            pattern: {
              value: REGEX_EMAIL,
              message:
                t(field.errorMessages?.pattern, language) ||
                _t("Invalid email address", language),
            },
          }
        : {}),
      ...(field.required
        ? {
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
      ...(field.validate
        ? {
            validate: field.validate,
          }
        : {}),
    };
  };

  const _arrayField = !!fieldArrayProps;

  const hasError = _arrayField
    ? !!errors[fieldArrayProps.fieldArrayName]?.[
        fieldArrayProps.arrayFieldIndex
      ]
    : !!errors[field.key];

  const errorMessage = _arrayField
    ? String(
        errors[fieldArrayProps.fieldArrayName]?.[
          fieldArrayProps.arrayFieldIndex
        ]?.value.message || ""
      )
    : String(errors[field.key]?.message || "");

  const hasConfirmError = _arrayField
    ? !!errors[`_${fieldArrayProps.fieldArrayName}`]?.[
        fieldArrayProps.arrayFieldIndex
      ]
    : !!errors[`_${field.key}`];

  const validateConfirm = (value, formValues) => {
    const originalValue = _arrayField
      ? formValues[fieldArrayProps.fieldArrayName]?.[
          fieldArrayProps.arrayFieldIndex
        ]?.value
      : formValues[field.key];
    return originalValue == value;
  };

  /* console.log({ hasError, errorMessage, hasConfirmError }); */

  return (
    <>
      <FormControl
        isInvalid={isDirty && hasError}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={field.required || field.confirm}
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        <div
          onMouseEnter={() => {
            field?.updateCurrentEventPreviewPart &&
              field?.updateCurrentEventPreviewPart(field.key);
          }}
          onMouseLeave={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart("DUMMY");
            }
          }}
        >
          <DynamicFormFieldHeader field={field} language={language} />
          <div className="flex flex-row items-center relative">
            {field.type === FormFieldType.TEXTAREA ? (
              <Textarea
                id={field.key}
                placeholder={t(field.placeholder, language)}
                as={TextareaAutosize}
                minRows={field.minRows}
                maxRows={field.maxRows}
                resize="none"
                {...register(field.key, {
                  ...toRegisterOptions(field),
                })}
              />
            ) : (
              <InputGroup>
                {field.leftAddon && (
                  <InputLeftAddon
                    children={field.leftAddon}
                    width={field.leftAddonWidth ? field.leftAddonWidth : "auto"}
                    height={
                      field.leftAddonHeight ? field.leftAddonHeight : "auto"
                    }
                  />
                )}
                <Input
                  id={field.key}
                  type={
                    field.type == FormFieldType.PASSWORD
                      ? show
                        ? "text"
                        : "password"
                      : field.type
                  }
                  placeholder={t(field.placeholder, language)}
                  {...register(field.key, {
                    ...toRegisterOptions(field),
                  })}
                  {...field.inputProps}
                />
                {field.rightAddon && (
                  <InputRightAddon children={field.rightAddon} />
                )}
              </InputGroup>
            )}
            {field.type == FormFieldType.PASSWORD && (
              <PasswordShowButton show={show} setShow={setShow} />
            )}
          </div>
        </div>

        <FormErrorMessage>{hasError && errorMessage}</FormErrorMessage>
      </FormControl>

      {field.confirm && (
        <FormControl
          isInvalid={isDirty && hasConfirmError}
          className={field.span ? colSpan : ""}
          isRequired={field.required || field.confirm}
          isDisabled={field.disabled}
          isReadOnly={field.readonly}
        >
          <div className="flex flex-row items-center text-base">
            <FormLabel htmlFor={`_${field.key}`} minH={6}>
              {_t("Confirm") + " " + t(field.label)}
            </FormLabel>
          </div>
          <FormHelperText>
            {field.note ? _t("retry enter to confirm") : ""}
          </FormHelperText>
          <div className="flex flex-row items-center relative h-fit">
            <Input
              id={`_${field.key}`}
              type={
                field.type == FormFieldType.PASSWORD
                  ? show
                    ? "text"
                    : "password"
                  : field.type
              }
              placeholder={t(field.placeholder)}
              {...register(`_${field.key}`, {
                ...toRegisterOptions(field),
                validate: validateConfirm,
              })}
            />
            {field.type == FormFieldType.PASSWORD && (
              <PasswordShowButton show={show} setShow={setShow} />
            )}
          </div>

          <FormErrorMessage>
            {hasConfirmError && _t("The input does not match")}
          </FormErrorMessage>
        </FormControl>
      )}
    </>
  );
};

export default DynamicFormInput;
